import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useProductSelectionStore, type MockupPosition } from '@/stores';
import { useProducts } from '@/hooks';
import {
  transformProduct,
  getConfiguredPositionTemplates,
} from '@/lib/product-transformer';
import type { UIProductMockupTemplate } from '@/lib/product-transformer';
import { MockupThumbnail } from '@/components/editor/MockupRenderer';
import { buildPrintContextParamsFromTemplate } from '@/lib/printContextBuilder';

/**
 * Position Tab with Mockup Thumbnail
 */
interface PositionTabProps {
  template: UIProductMockupTemplate;
  active: boolean;
  onClick: () => void;
  backgroundColor?: string;
}

// Locale key per known position; unknown/future position keys fall back to
// a capitalized rendering of the raw key.
const POSITION_LABEL_KEYS: Record<string, string> = {
  front: 'creator.positions.front',
  back: 'creator.positions.back',
  'back-inside': 'creator.positions.backInside',
  'sleeve-left': 'creator.positions.sleeveLeft',
  'sleeve-right': 'creator.positions.sleeveRight',
};

function formatLabel(key: string, t: (key: string) => string) {
  if (POSITION_LABEL_KEYS[key]) return t(POSITION_LABEL_KEYS[key]);
  return key
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function PositionTab({
  template,
  active,
  onClick,
  backgroundColor = '#FFFFFF',
}: PositionTabProps) {
  const { t } = useTranslation();
  const label = formatLabel(template.positionKey, t);

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 px-3 py-1.5 rounded-control transition-all',
        active
          ? 'bg-primary-50 ring-2 ring-primary'
          : 'hover:bg-[var(--paper-2)]'
      )}
    >
      {/* Thumbnail with 5-layer MockupRenderer */}
      <div
        className={cn(
          'w-12 h-14 rounded border overflow-hidden',
          active ? 'border-primary' : 'border-[var(--line)]'
        )}
      >
        <MockupThumbnail
          template={template}
          backgroundColor={backgroundColor}
          width={48}
          height={56}
        />
      </div>
      {/* Label */}
      <span
        className={cn(
          'dc-type-caption whitespace-nowrap',
          active ? 'text-primary' : 'text-[var(--mute)]'
        )}
      >
        {label}
      </span>
    </button>
  );
}

/**
 * Bottom Bar component - Position switcher with mockup thumbnails
 *
 * Shows available positions (Front, Back, etc.) for the focused product
 * with small mockup thumbnails for each position.
 */
export function BottomBar() {
  const { t } = useTranslation();
  // Get focused product and position state
  const focusedProductId = useProductSelectionStore(
    (state) => state.focusedProductId
  );
  const focusedColorId = useProductSelectionStore(
    (state) => state.focusedColorId
  );
  const activePosition = useProductSelectionStore(
    (state) => state.activePosition
  );
  const setActivePosition = useProductSelectionStore(
    (state) => state.setActivePosition
  );

  const { data: apiProducts } = useProducts();

  // Get the focused product with mockup templates
  const focusedProduct = useMemo(() => {
    if (!focusedProductId || !apiProducts) return null;

    const apiProduct = apiProducts.find((p) => p.id === focusedProductId);
    if (!apiProduct) return null;

    return transformProduct(apiProduct);
  }, [focusedProductId, apiProducts]);

  // Get the selected color for background
  const selectedColor = useMemo(() => {
    if (!focusedProduct) return null;

    if (focusedColorId) {
      const color = focusedProduct.colors.find((c) => c.id === focusedColorId);
      if (color) return color;
    }

    return focusedProduct.colors[0] || null;
  }, [focusedProduct, focusedColorId]);

  // One tab per position the product configures, in display order
  const positionTemplates = useMemo(
    () =>
      focusedProduct
        ? getConfiguredPositionTemplates(focusedProduct.mockupTemplates)
        : [],
    [focusedProduct]
  );

  const backgroundColor = selectedColor?.hex || '#FFFFFF';

  // Without a focused product (or one without a calibrated template) there
  // is no print area to switch to: the bar stays empty instead of offering
  // tabs on an invented area (R13).
  if (!focusedProduct || positionTemplates.length === 0) {
    return (
      <div className="h-14 bg-[var(--paper)] border-t border-[var(--line)] flex items-center px-6 shrink-0" />
    );
  }

  return (
    <div className="h-[100px] bg-[var(--paper)] border-t border-[var(--line)] flex items-center px-6 shrink-0">
      {/* Position Tabs with Mockup Thumbnails */}
      <div className="flex items-center gap-3">
        {positionTemplates.map((template) => (
          <PositionTab
            key={template.id}
            template={template}
            active={activePosition === template.positionKey}
            onClick={() =>
              setActivePosition(
                template.positionKey as MockupPosition,
                template.productPositionId,
                buildPrintContextParamsFromTemplate(template)
              )
            }
            backgroundColor={backgroundColor}
          />
        ))}
      </div>

      {/* Product info */}
      {focusedProduct && (
        <span className="ml-auto text-sm text-[var(--mute)]">
          {t('creator.bottomBar.productSummary', {
            name: focusedProduct.name,
            count: positionTemplates.length,
          })}
        </span>
      )}
    </div>
  );
}
